import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type {
  BalanceViewGroupBy,
  BalanceViewMode,
  BalanceViewTarget,
  BalanceViewRounding,
  BalanceViewChartGroupBy,
  BalanceViewCumulativeBalance,
} from 'src/store/shared/balance-table-opts/view-options';
import type { SliceFactoryProps } from 'src/store/shared/types';
import type { BalanceTableOptsState } from './types';

export function initBalanceTableOptsState(opts?: Partial<BalanceTableOptsState>) {
  const initialState: BalanceTableOptsState = {
    isSmallBalancesVisible: false,
    isDuplicatesVisible: true,
    isInitialBalanceUsed: false,
    viewMode: 'table',
    viewGroupBy: 'type',
    viewChartGroupBy: 'accounts',
    viewCumulativeBalance: false,
    showSelectedBalance: false,
    viewTarget: 'volume',
    viewRounding: 'on',
    isIgnoreOtherTokens: false,
    ignoreTokens: [],
    showCurrentAccounts: false,
    shouldShowFullName: false,
    showDetails: false,
    showErrorAccounts: true,
    excludeFuturesFromSummary: false,
    applyTokenMapping: true,
  };

  return { ...initialState, ...opts };
}

export function initBalanceTableOptsSlice({
  sliceName = 'balanceTableOpts',
  initialState = initBalanceTableOptsState(),
}: SliceFactoryProps<BalanceTableOptsState, never>) {
  const balanceTableOptsSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
      setInitialState(state, action: PayloadAction<Partial<BalanceTableOptsState>>) {
        state.viewMode = action.payload.viewMode ?? initialState.viewMode;
      },
      toggleSmallBalances(state) {
        // Pure visibility toggle: token selection (`ignoreTokens`) is an
        // independent axis and is intentionally left untouched here.
        state.isSmallBalancesVisible = !state.isSmallBalancesVisible;
      },
      toggleDuplicates(state) {
        state.isDuplicatesVisible = !state.isDuplicatesVisible;
      },
      toggleInitialBalanceUsed(state) {
        state.isInitialBalanceUsed = !state.isInitialBalanceUsed;
      },
      toggleViewGroupBy(state, action: PayloadAction<BalanceViewGroupBy>) {
        state.viewGroupBy = action.payload;
      },
      toggleViewMode(state, action: PayloadAction<BalanceViewMode>) {
        state.viewMode = action.payload;
      },
      toggleViewTarget(state, action: PayloadAction<BalanceViewTarget>) {
        state.viewTarget = action.payload;
      },
      toggleViewRounding(state, action: PayloadAction<BalanceViewRounding>) {
        state.viewRounding = action.payload;
      },
      toggleViewChartGroupBy(state, action: PayloadAction<BalanceViewChartGroupBy>) {
        state.viewChartGroupBy = action.payload;
      },
      toggleViewCumulativeBalance(state, action: PayloadAction<BalanceViewCumulativeBalance>) {
        state.viewCumulativeBalance = action.payload;
      },
      toggleShowSelectedBalance(state, action: PayloadAction<boolean>) {
        state.showSelectedBalance = action.payload;
      },
      toggleIsIgnoreOtherTokens(state) {
        state.isIgnoreOtherTokens = !state.isIgnoreOtherTokens;
        state.ignoreTokens = [];
      },
      toggleShowCurrentAccounts(state) {
        state.showCurrentAccounts = !state.showCurrentAccounts;
      },
      toggleIgnoreTokens(state, action: PayloadAction<string | string[]>) {
        const { ignoreTokens } = state;
        const payload = action.payload;

        if (Array.isArray(payload)) {
          const allIncluded = payload.every((token) => ignoreTokens.includes(token));
          if (allIncluded) {
            state.ignoreTokens = ignoreTokens.filter((token) => !payload.includes(token));
          } else {
            state.ignoreTokens = [
              ...ignoreTokens,
              ...payload.filter((token) => !ignoreTokens.includes(token)),
            ];
          }
        } else {
          if (ignoreTokens.includes(payload)) {
            state.ignoreTokens = ignoreTokens.filter((token) => token !== payload);
          } else {
            state.ignoreTokens = [...ignoreTokens, payload];
          }
        }
      },
      toggleShowFullName(state) {
        state.shouldShowFullName = !state.shouldShowFullName;
      },
      resetIgnoreTokens(state) {
        state.ignoreTokens = [];
      },
      toggleShowDetails(state) {
        state.showDetails = !state.showDetails;
      },
      toggleShowErrorAccounts(state) {
        state.showErrorAccounts = !state.showErrorAccounts;
      },
      toggleExcludeFuturesFromSummary(state) {
        state.excludeFuturesFromSummary = !state.excludeFuturesFromSummary;
      },
      toggleApplyTokenMapping(state) {
        state.applyTokenMapping = !state.applyTokenMapping;
      },
    },
  });

  const actions = {
    ...balanceTableOptsSlice.actions,
  };

  return {
    actions,
    reducer: balanceTableOptsSlice.reducer,
  };
}
